import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Star, CheckCircle2, MessageSquare, ShieldCheck, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { FeedbackModal } from './FeedbackModal';
const testimonials = [{
  id: 1,
  quote: "I thought I was going crazy. This tool showed me the exact patterns of gaslighting in my chat logs. It's like having a forensic psychologist in your pocket.",
  user: '@sarah_j92',
  verified: true,
  rating: 5
}, {
  id: 2,
  quote: "The 'Silent Drain' detection was spot on. It flagged passive-aggressive comments I had completely normalized over 3 years.",
  user: '@mark_d_dev',
  verified: true,
  rating: 5
}, {
  id: 3,
  quote: 'Finally, objective proof. I showed the report to my therapist and she agreed with 95% of the findings. Validating is an understatement.',
  user: '@em_watson_x',
  verified: true,
  rating: 5
}, {
  id: 4,
  quote: "Scary accurate. It picked up on the 'DARVO' tactics my ex was using to flip the blame. Worth every penny for the sanity check.",
  user: '@alex_rider88',
  verified: true,
  rating: 5
}, {
  id: 5,
  quote: 'I use this for my dating app matches now. Saved me from a potential narcissist last week. The red flags were subtle but the AI caught them.',
  user: '@jenna_b_nyc',
  verified: true,
  rating: 4
}, {
  id: 6,
  quote: "The visual breakdown of toxicity levels helped me explain to my friends why I had to leave. Data doesn't lie.",
  user: '@chris_m_uk',
  verified: true,
  rating: 5
}];
export function SocialProofSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Auto-rotation logic
  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isHovered]);
  const handleNext = () => {
    setActiveIndex(prev => (prev + 1) % testimonials.length);
  };
  const handlePrev = () => {
    setActiveIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1);
  };
  // Calculate rotation for 3D carousel
  // 6 items = 60 degrees each
  const theta = 360 / testimonials.length;
  const radius = 400; // Distance from center
  return <section className="py-24 bg-[#050505] relative overflow-hidden min-h-[800px] flex flex-col justify-center" data-id="element-3004">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-30 pointer-events-none" data-id="element-3005" />

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full" data-id="element-3006">
        {/* Trust Bar Header */}
        <div className="text-center mb-16 md:mb-24" data-id="element-3007">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-4 bg-[#0A0A0A] border border-gray-800 rounded-full px-6 py-3 mb-8 shadow-[0_0_30px_rgba(0,0,0,0.5)]" data-id="element-3008">
            <div className="flex gap-1" data-id="element-3009">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} size={16} className={`${star === 5 ? 'text-[#FFD700]/50' : 'text-[#FFD700] fill-[#FFD700]'}`} data-id="element-3010" />)}
            </div>
            <div className="h-4 w-px bg-gray-800" data-id="element-3011" />
            <span className="text-gray-300 font-mono text-xs uppercase tracking-widest font-bold" data-id="element-3012">
              <span className="text-white" data-id="element-3013">4.9/5</span> • Validated by 10,000+
              Sanity Checks
            </span>
            <div className="h-4 w-px bg-gray-800" data-id="element-3014" />
            <ShieldCheck size={16} className="text-[#DC2626]" data-id="element-3015" />
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-6" data-id="element-3016">
            Field Reports from <br data-id="element-3017" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DC2626] to-red-500" data-id="element-3018">
              The Front Lines
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-gray-400 font-mono text-sm max-w-2xl mx-auto" data-id="element-3019">
            Real stories from people who reclaimed their reality using forensic
            AI analysis.
          </motion.p>
        </div>

        {/* 3D Carousel Container */}
        <div className="relative h-[450px] md:h-[500px] w-full flex items-center justify-center perspective-1000 mb-16" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{
        perspective: '1200px'
      }} data-id="element-3020">
          {/* Desktop 3D Carousel */}
          <motion.div className="relative w-[320px] md:w-[400px] h-[350px] md:h-[400px] preserve-3d hidden md:block" animate={{
          rotateY: activeIndex * -theta
        }} transition={{
          type: 'spring',
          stiffness: 50,
          damping: 20,
          mass: 1
        }} style={{
          transformStyle: 'preserve-3d'
        }} data-id="element-3021">
            {testimonials.map((item, index) => {
            // Calculate if this card is the active one
            // We need to handle the circular nature of the index
            const isActive = index === activeIndex;
            return <div key={item.id} className="absolute inset-0 bg-[#0A0A0A] border border-gray-800 p-8 rounded-xl backface-visible" style={{
              transform: `rotateY(${index * theta}deg) translateZ(${radius}px)`,
              // Fix backface visibility issue in some browsers
              backfaceVisibility: 'hidden'
            }} data-id="element-3022">
                  {/* Active State Glow/Border */}
                  <motion.div className="absolute inset-0 border-2 rounded-xl pointer-events-none" animate={{
                borderColor: isActive ? '#DC2626' : 'transparent',
                boxShadow: isActive ? '0 0 30px rgba(220, 38, 38, 0.3)' : 'none',
                opacity: isActive ? 1 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-3023" />

                  {/* Content Container - Fade out when inactive */}
                  <motion.div className="h-full flex flex-col" animate={{
                opacity: isActive ? 1 : 0.3,
                filter: isActive ? 'blur(0px)' : 'blur(2px)',
                scale: isActive ? 1 : 0.95
              }} transition={{
                duration: 0.3
              }} data-id="element-3024">
                    {/* Corner Brackets */}
                    <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-700 transition-colors" data-id="element-3025" />
                    <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-700 transition-colors" data-id="element-3026" />
                    <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-700 transition-colors" data-id="element-3027" />
                    <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-700 transition-colors" data-id="element-3028" />

                    {/* Header */}
                    <div className="flex justify-between items-start mb-6" data-id="element-3029">
                      <div className="flex gap-1" data-id="element-3030">
                        {[...Array(5)].map((_, i) => <Star key={i} size={14} className={`${i < item.rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-gray-800'}`} data-id="element-3031" />)}
                      </div>
                      {item.verified && <div className="flex items-center gap-1.5 bg-green-900/20 border border-green-900/50 px-2 py-1 rounded" data-id="element-3032">
                          <CheckCircle2 size={10} className="text-green-500" data-id="element-3033" />
                          <span className="text-[10px] font-bold text-green-500 uppercase tracking-wider" data-id="element-3034">
                            Verified
                          </span>
                        </div>}
                    </div>

                    {/* Quote */}
                    <p className="text-gray-300 leading-relaxed mb-6 text-sm md:text-base flex-grow" data-id="element-3035">
                      "{item.quote}"
                    </p>

                    {/* User Info */}
                    <div className="flex items-center gap-3 border-t border-gray-800 pt-4 mt-auto" data-id="element-3036">
                      <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center border border-gray-800 flex-shrink-0" data-id="element-3037">
                        <User size={16} className="text-gray-500" data-id="element-3038" />
                      </div>
                      <div data-id="element-3039">
                        <p className="text-gray-400 font-mono text-xs font-bold" data-id="element-3040">
                          {item.user}
                        </p>
                        <p className="text-gray-600 text-[10px] uppercase tracking-wider" data-id="element-3041">
                          Encrypted User ID
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>;
          })}
          </motion.div>

          {/* Mobile Fallback (Simple Slider) */}
          <div className="md:hidden w-full h-full relative overflow-hidden" data-id="element-3042">
            <motion.div className="flex h-full" animate={{
            x: `-${activeIndex * 100}%`
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-3043">
              {testimonials.map(item => <div key={item.id} className="w-full h-full flex-shrink-0 px-4 flex items-center justify-center" data-id="element-3044">
                  <div className="bg-[#0A0A0A] border border-gray-800 p-8 rounded-xl w-full max-w-sm relative h-auto min-h-[300px] flex flex-col" data-id="element-3045">
                    {/* Active Border for Mobile */}
                    <div className="absolute inset-0 border border-[#DC2626]/30 rounded-xl pointer-events-none shadow-[0_0_15px_rgba(220,38,38,0.1)]" data-id="element-3046" />

                    {/* Corner Brackets */}
                    <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#DC2626] transition-colors" data-id="element-3047" />
                    <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#DC2626] transition-colors" data-id="element-3048" />
                    <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#DC2626] transition-colors" data-id="element-3049" />
                    <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#DC2626] transition-colors" data-id="element-3050" />

                    {/* Header */}
                    <div className="flex justify-between items-start mb-6" data-id="element-3051">
                      <div className="flex gap-1" data-id="element-3052">
                        {[...Array(5)].map((_, i) => <Star key={i} size={14} className={`${i < item.rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-gray-800'}`} data-id="element-3053" />)}
                      </div>
                      {item.verified && <div className="flex items-center gap-1.5 bg-green-900/20 border border-green-900/50 px-2 py-1 rounded" data-id="element-3054">
                          <CheckCircle2 size={10} className="text-green-500" data-id="element-3055" />
                          <span className="text-[10px] font-bold text-green-500 uppercase tracking-wider" data-id="element-3056">
                            Verified
                          </span>
                        </div>}
                    </div>

                    {/* Quote */}
                    <p className="text-gray-300 leading-relaxed mb-6 text-sm flex-grow" data-id="element-3057">
                      "{item.quote}"
                    </p>

                    {/* User Info */}
                    <div className="flex items-center gap-3 border-t border-gray-800 pt-4 mt-auto" data-id="element-3058">
                      <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center border border-gray-800 flex-shrink-0" data-id="element-3059">
                        <User size={16} className="text-gray-500" data-id="element-3060" />
                      </div>
                      <div data-id="element-3061">
                        <p className="text-gray-400 font-mono text-xs font-bold" data-id="element-3062">
                          {item.user}
                        </p>
                        <p className="text-gray-600 text-[10px] uppercase tracking-wider" data-id="element-3063">
                          Encrypted User ID
                        </p>
                      </div>
                    </div>
                  </div>
                </div>)}
            </motion.div>
          </div>

          {/* Navigation Arrows */}
          <button onClick={handlePrev} className="absolute left-0 md:left-10 top-1/2 -translate-y-1/2 z-20 p-3 bg-[#0A0A0A] border border-gray-800 rounded-full text-gray-400 hover:text-white hover:border-[#DC2626] transition-all hover:scale-110 shadow-lg" aria-label="Previous testimonial" data-id="element-3064">
            <ChevronLeft size={24} data-id="element-3065" />
          </button>
          <button onClick={handleNext} className="absolute right-0 md:right-10 top-1/2 -translate-y-1/2 z-20 p-3 bg-[#0A0A0A] border border-gray-800 rounded-full text-gray-400 hover:text-white hover:border-[#DC2626] transition-all hover:scale-110 shadow-lg" aria-label="Next testimonial" data-id="element-3066">
            <ChevronRight size={24} data-id="element-3067" />
          </button>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-3 mb-16" data-id="element-3068">
          {testimonials.map((_, index) => <button key={index} onClick={() => setActiveIndex(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activeIndex ? 'bg-[#DC2626] w-6' : 'bg-gray-800 hover:bg-gray-600'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-3069" />)}
        </div>

        {/* CTA */}
        <div className="text-center" data-id="element-3070">
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => setIsModalOpen(true)} className="group relative inline-flex items-center gap-3 bg-[#DC2626] text-white font-black py-4 px-8 uppercase tracking-widest text-sm hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(220,38,38,0.4)] hover:shadow-[0_0_30px_rgba(220,38,38,0.6)]" data-id="element-3071">
            <MessageSquare size={18} data-id="element-3072" />
            <span data-id="element-3073">Share Your Story</span>
            <div className="absolute inset-0 border-2 border-white/20 group-hover:border-white/40 transition-colors" data-id="element-3074" />
          </motion.button>
          <p className="mt-4 text-gray-500 text-xs font-mono" data-id="element-3075">
            Your feedback helps improve our detection accuracy.
          </p>
        </div>
      </div>

      {/* Modal */}
      <FeedbackModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-3076" />
    </section>;
}